import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  IconButton,
  Box,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useStore } from "../StoreContext/StoreContext";
import { OrderUpdateApi, OrderCreateApi, ProductListAPI } from "../../Utility/UtilApi/UtilApi";
import UtilToastMessages from "../../Utility/UtilToastMessages/UtilToastMessages";
import { useNavigate, Link } from "react-router-dom";
import { buttonColor, buttonHoverColor, fontFamilyall } from "../../UtilColorPalate/UtilColorPalate";

const WishlistPage = () => {
  const {
    wishListItems,
    hasExistingOrder,
    OrderRecordId,
    dealLineItems,
    hasExistingWishList,
    wishListRecordId,
    fetchCurrentDeals,
    currencyCode,
    isLoggedIn,
    localCartItems,
    updateLocalCart,
    localWishlist,
    updateLocalWishlist,
    getAndUpdateCartCount,
  } = useStore();

  const navigate = useNavigate();
  const [isSnackbarOpen, setSnackbarOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastColor, setToastColor] = useState("");
  const [enrichedWishlistItems, setEnrichedWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasFetchedProducts = useRef(false);
  const cachedProducts = useRef([]);

  // Sync localWishlist with localStorage on mount for unauthenticated users
  useEffect(() => {
    if (!isLoggedIn) {
      const storedWishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
      setEnrichedWishlistItems(storedWishlist);
      setLoading(true); // Trigger enrichment
    }
  }, [isLoggedIn]);


      useEffect(() => {
        window.scrollTo(0, 0); // Scroll to top on mount
      }, []);
  // Enrich wishlist items with product details
  const enrichWishlistItems = async () => {
    setLoading(true);
    let products = cachedProducts.current;
    let itemsToEnrich = isLoggedIn ? wishListItems : localWishlist;

    if (!hasFetchedProducts.current) {
      try {
        const productResponse = await ProductListAPI();
        console.log("ProductListApi response:", productResponse);
        products = Array.isArray(productResponse.data.products)
          ? productResponse.data.products
          : [];
        cachedProducts.current = products;
        hasFetchedProducts.current = true;
      } catch (error) {
        console.error("Error fetching product list:", error);
        setToastMessage("Failed to load product details.");
        setToastColor("error");
        setSnackbarOpen(true);
        products = [];
      }
    }

    const enrichedItems = itemsToEnrich.map((wishlistItem) => {
      const productId = wishlistItem.product?.id || wishlistItem.product || wishlistItem.id;
      const matchingProduct = products.find((product) => product.id === productId) || {};
      return {
        ...wishlistItem,
        id: matchingProduct.id || productId,
        imageone: matchingProduct.imageone || "https://via.placeholder.com/200",
        imagetwo: matchingProduct.imagetwo || "",
        imagethree: matchingProduct.imagethree || "",
        imagefour: matchingProduct.imagefour || "",
        imagefive: matchingProduct.imagefive || "",
        description: matchingProduct.description || "No description available",
        productcategories: matchingProduct.productcategories || [],
        selectedImage:
          matchingProduct.imagefive || matchingProduct.imageone || "https://via.placeholder.com/200",
        name: matchingProduct.name || wishlistItem.name || "Unknown Product",
        unitpriceexclvat: matchingProduct.unitpriceexclvat || wishlistItem.unitpriceexclvat || 0,
        unitpriceincldiscount: matchingProduct.unitpriceincldiscount || wishlistItem.unitpriceincldiscount || 0,

        vat: matchingProduct.vat || wishlistItem.vat || 0,
        discount: matchingProduct.discount || wishlistItem.discount || 0,
        quantity: wishlistItem.quantity || 1,
        recordid: wishlistItem.recordid || null, // Only for server-side items
      };
    });
    setEnrichedWishlistItems(enrichedItems);
    setLoading(false);
  };

  useEffect(() => {
    enrichWishlistItems();
  }, [wishListItems, localWishlist, isLoggedIn]);

  const formatPrice = (value) => {
    const formattedValue = value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${currencyCode || "$"}${formattedValue}`;
  };

  const createDeal = async (product, type, qty) => {
    const generateName = () => `${type}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const payload = {
      order: {
        name: generateName(),
        type: type,
        stage: "In Progress",
      },
      deallineitems: [
        {
          name: product.name,
          product: product.id,
          quantity: qty,
          unitpriceexclvat: product.unitpriceexclvat,
          vat: product.vat || 0,
          discount: product.discount || 0,
        },
      ],
    };

    try {
      const response = await OrderCreateApi(payload);
      console.log(`${type} created:`, response.data);
      setToastMessage(response.message || `${type} created and product added!`);
      setToastColor(response.success ? "success" : "warning");
      setSnackbarOpen(true);
      await fetchCurrentDeals();
      return response.data;
    } catch (error) {
      console.error(`Error creating ${type}:`, error);
      setToastMessage(`Failed to create ${type}.`);
      setToastColor("error");
      setSnackbarOpen(true);
      return null;
    }
  };

  const updateDeal = async (product, type, recordId, existingItems, qty, isAdding = true) => {
    if (!recordId) {
      console.error(`${type} record ID is missing`);
      setToastMessage(`Error: No ${type} ID available.`);
      setToastColor("error");
      setSnackbarOpen(true);
      return;
    }

    let updatedLineItems = existingItems.map((item) => ({
      recordid: item.recordid,
      name: item.name,
      product: item.product.id || item.product,
      quantity: item.quantity,
      unitpriceexclvat: item.unitpriceexclvat,
      vat: item.vat || 0,
      discount: item.discount || 0,
    }));

    const existingLineItemIndex = updatedLineItems.findIndex(
      (item) => item.product === product.id
    );

    if (isAdding) {
      if (existingLineItemIndex !== -1) {
        updatedLineItems[existingLineItemIndex].quantity += qty;
      } else {
        updatedLineItems.push({
          name: product.name,
          product: product.id,
          quantity: qty,
          unitpriceexclvat: product.unitpriceexclvat,
          vat: product.vat || 0,
          discount: product.discount || 0,
        });
      }
    } else {
      if (existingLineItemIndex === -1) return;
      updatedLineItems = updatedLineItems.filter((item) => item.product !== product.id);
    }

    const payload = {
      order: {
        recordid: recordId,
        type: type,
        stage: "In Progress",
      },
      deallineitems: updatedLineItems,
      ...(isAdding ? {} : { remove: [existingItems[existingLineItemIndex].recordid] }),
    };

    try {
      const response = await OrderUpdateApi(payload);
      console.log(`${type} updated:`, response.data);
      setToastMessage(response.message || `Product ${isAdding ? "added to" : "removed from"} ${type}!`);
      setToastColor(response.success ? "success" : "warning");
      setSnackbarOpen(true);
      await fetchCurrentDeals();
      return response.data;
    } catch (error) {
      console.error(`Error updating ${type}:`, error);
      setToastMessage(`Failed to update ${type}.`);
      setToastColor("error");
      setSnackbarOpen(true);
      return null;
    }
  };

  const handleAddToCart = async (item) => {
    if (!isLoggedIn) {
      const cartItem = {
        id: item.id,
        name: item.name,
        unitpriceexclvat: item.unitpriceexclvat,
        vat: item.vat || 0,
        discount: item.discount || 0,
        quantity: 1,
      };

      const existingItem = localCartItems.find((cartItem) => cartItem.id === item.id);
      let updatedCart;

      if (existingItem) {
        updatedCart = localCartItems.map((cartItem) =>
          cartItem.id === item.id ? { ...cartItem, quantity: cartItem.quantity + 1 } : cartItem
        );
      } else {
        updatedCart = [...localCartItems, cartItem];
      }

      updateLocalCart(updatedCart);
      setToastMessage("Product added to cart!");
      setToastColor("success");
      setSnackbarOpen(true);
    } else {
      try {
        if (!hasExistingOrder) {
          await createDeal(item, "Order", 1);
        } else {
          await updateDeal(item, "Order", OrderRecordId, dealLineItems, 1);
        }
      } catch (error) {
        console.error("Error in handleAddToCart:", error);
        setToastMessage("Failed to add product to cart.");
        setToastColor("error");
        setSnackbarOpen(true);
      }
    }
  };

  const handleRemoveFromWishlist = async (item) => {
    if (!isLoggedIn) {
      // For unauthenticated users, clear the entire wishlist
      updateLocalWishlist([]); // Set local wishlist to empty array
      setEnrichedWishlistItems([]); // Clear enriched wishlist items
      localStorage.setItem("wishlist", JSON.stringify([])); // Update localStorage to reflect empty wishlist
      setToastMessage("Wishlist cleared!");
      setToastColor("success");
      setSnackbarOpen(true);
    } else {
      // For authenticated users, remove only the specific item
      if (!wishListRecordId) return;
  
      try {
        await updateDeal(item, "Wish List", wishListRecordId, wishListItems, 0, false);
        setEnrichedWishlistItems((prev) => prev.filter((enrichedItem) => enrichedItem.id !== item.id));
      } catch (error) {
        console.error("Error removing item:", error);
        setToastMessage("Failed to remove item from wishlist.");
        setToastColor("error");
        setSnackbarOpen(true);
      }
    }
  };

  const isProductInCart = (productId) => {
    if (!isLoggedIn) {
      return localCartItems.some((item) => item.id === productId);
    }
    return hasExistingOrder && dealLineItems.some(
      (item) => (item.product.id || item.product) === productId
    );
  };

  const handleGoToCart = () => {
    navigate("/cart");
  };

return (
    <Container sx={{ padding: "20px", fontFamily: "Poppins, sans-serif", mt: 20 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold", mb: 3, fontFamily: fontFamilyall }}>
        Your Wishlist
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
          <CircularProgress size={60} sx={{ color: "#8c7aa2" }} />
        </Box>
      ) : enrichedWishlistItems.length === 0 ? (
        <Typography variant="h6" sx={{ textAlign: "center", color: "#555", fontFamily: fontFamilyall }}>
          Your wishlist is empty. <Link to="/shop">Start shopping now!</Link>
        </Typography>
      ) : (
        <Grid container spacing={4}>
          {enrichedWishlistItems.map((item) => {
            const images = [
              item.imageone,
              item.imagetwo,
              item.imagethree,
              item.imagefour,
              item.imagefive,
            ].filter((img) => img && img.trim() !== "");
            const categoryNames = (item.productcategories || []).map((cat) => cat.name).join(", ");

            return (
              <Grid item xs={12} md={6} key={item.recordid || item.id}>
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    borderRadius: "12px",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2, p: 2 }}>
                    {images.length > 1 && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "80px" }}>
                        {images.map((img, idx) => (
                          <Card
                            key={idx}
                            sx={{
                              width: 60,
                              height: 60,
                              cursor: "pointer",
                              border: item.selectedImage === img ? "2px solid #8c7aa2" : "none",
                              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                            }}
                            onClick={() => {
                              const updatedItems = enrichedWishlistItems.map((i) =>
                                (i.recordid || i.id) === (item.recordid || item.id)
                                  ? { ...i, selectedImage: img }
                                  : i
                              );
                              setEnrichedWishlistItems(updatedItems);
                            }}
                          >
                            <CardMedia
                              component="img"
                              image={img}
                              alt={`${item.name} thumbnail ${idx + 1}`}
                              sx={{ objectFit: "cover", width: "100%", height: "100%" }}
                            />
                          </Card>
                        ))}
                      </Box>
                    )}
                    <CardMedia
                      component="img"
                      height="200"
                      image={item.selectedImage || images[0]}
                      alt={item.name}
                      sx={{
                        objectFit: "contain",
                        width: "200px",
                        height: "200px",
                        backgroundColor: "#f5f5f5",
                        transition: "transform 0.3s ease",
                        "&:hover": { transform: "scale(1.02)" },
                      }}
                    />
                  </Box>
                  <CardContent sx={{ flex: 1, p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                      <Typography variant="h5" sx={{ fontWeight: "600", color: "#333", fontFamily: fontFamilyall }}>
                        {item.name}
                      </Typography>
                      {item?.discount && item.discount > 0 ? (
                        <Typography
                          sx={{
                            fontFamily: fontFamilyall,
                            backgroundColor: "#ff4444",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "12px",
                            fontSize: "14px",
                            fontWeight: "bold",
                            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                            animation: "pulse 1.5s infinite",
                            "@keyframes pulse": {
                              "0%": { transform: "scale(1)" },
                              "50%": { transform: "scale(1.05)" },
                              "100%": { transform: "scale(1)" },
                            },
                          }}
                        >
                          {item.discount}% OFF
                        </Typography>
                      ) : null}
                    </Box>
                    <Typography variant="body1" sx={{ color: "#555", mb: 1, fontFamily: fontFamilyall }}>
                      Category: {categoryNames || "N/A"}
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#555", mb: 2, fontFamily: fontFamilyall }}>
                      {item.description}
                    </Typography>
                    {item?.discount >= 0 ? (
                      <>
                        <Typography
                          variant="body1"
                          sx={{
                            color: "red",
                            mb: 1,
                            textDecoration: "line-through",
                            fontFamily: fontFamilyall,
                          }}
                        >
                          Price: {formatPrice(item.unitpriceexclvat || "N/A")}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            color: "#8c7aa2",
                            fontWeight: "bold",
                            mb: 2,
                            fontSize: "1.25rem",
                            fontFamily: fontFamilyall,
                          }}
                        >
                          Discounted Price: {formatPrice(item.unitpriceincldiscount || "N/A")}
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        variant="h6"
                        sx={{ color: "#8c7aa2", mb: 2, fontFamily: fontFamilyall }}
                      >
                        {formatPrice(item.unitpriceexclvat || "N/A")}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      {isProductInCart(item.id) ? (
                        <Typography
                          component="span"
                          sx={{
                            fontFamily: fontFamilyall,
                            backgroundColor: "#4CAF50",
                            color: "#fff",
                            textTransform: "none",
                            padding: {
                              xs: "4px 10px",
                              sm: "5px 15px",
                            },
                            borderRadius: "4px",
                            fontSize: {
                              xs: "0.75rem",
                              sm: "0.875rem",
                            },
                            minWidth: {
                              xs: "80px",
                              sm: "100px",
                            },
                            display: "inline-block",
                          }}
                        >
                          Added to Cart
                        </Typography>
                      ) : (
                        <Button
                          variant="contained"
                          onClick={() => handleAddToCart(item)}
                          sx={{
                            backgroundColor: buttonColor,
                            color: "#fff",
                            textTransform: "none",
                            padding: "5px 15px",
                            borderRadius: "4px",
                            "&:hover": { backgroundColor: buttonHoverColor },
                            fontFamily: fontFamilyall,
                          }}
                        >
                          Add to Cart
                        </Button>
                      )}
                      <Tooltip title="Remove from Wishlist">
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveFromWishlist(item)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <UtilToastMessages
        isOpen={isSnackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        data={toastMessage}
        severity={toastColor}
      />
    </Container>
  );
};

export default WishlistPage;